import { FC } from 'react';
import { Box, Text, VStack, HStack } from '@chakra-ui/react';
import { Dog } from '@/types/dog';

/**
 * 表示サイズ
 */
export type DogIdentitySize = 'sm' | 'md' | 'lg';

/**
 * DogIdentityLabel が必要とする犬の最小フィールド
 */
export type DogIdentityInfo = Pick<
  Dog,
  | 'officialName'
  | 'name'
  | 'jkcRegistrationNumber'
  | 'dnaRegistrationNumber'
  | 'microchipNumber'
  | 'microchipPin'
>;

interface DogIdentityLabelProps {
  /** 表示対象の犬（必要フィールドのみ） */
  dog: DogIdentityInfo;
  /** 表示サイズ */
  size?: DogIdentitySize;
  /** 登録番号（JKC/DNA/マイクロチップ/暗証記号）を表示するか */
  showRegistrationNumbers?: boolean;
  /** どうぶつ名（通称）を表示するか（デフォルト true） */
  showAnimalName?: boolean;
}

/** メイン名（正式名称）のフォントサイズ */
const MAIN_FONT_SIZE: Record<DogIdentitySize, string> = {
  sm: 'sm',
  md: 'md',
  lg: 'xl',
};

/** どうぶつ名（補助表示）のフォントサイズ */
const SUB_FONT_SIZE: Record<DogIdentitySize, string> = {
  sm: 'xs',
  md: 'sm',
  lg: 'sm',
};

/**
 * 登録番号の表示用エントリを構築
 */
const buildRegistrationEntries = (
  dog: DogIdentityInfo
): Array<{ label: string; value: string }> => {
  const entries: Array<{ label: string; value: string }> = [];
  if (dog.jkcRegistrationNumber) {
    entries.push({ label: 'JKC', value: dog.jkcRegistrationNumber });
  }
  if (dog.dnaRegistrationNumber) {
    entries.push({ label: 'DNA', value: dog.dnaRegistrationNumber });
  }
  if (dog.microchipNumber) {
    entries.push({ label: 'MC', value: dog.microchipNumber });
  }
  if (dog.microchipPin) {
    entries.push({ label: '暗証記号', value: dog.microchipPin });
  }
  return entries;
};

/**
 * 「正式名称（officialName） + どうぶつ名（name） + 登録番号」を
 * 1 セットとして統一表示する共通コンポーネント。
 *
 * - officialName を強調表示（メイン）
 * - name を補助表示（どうぶつ名）
 * - officialName が空の場合は name をメインに表示するフォールバック
 */
export const DogIdentityLabel: FC<DogIdentityLabelProps> = ({
  dog,
  size = 'md',
  showRegistrationNumbers = false,
  showAnimalName = true,
}) => {
  // officialName が空の場合は name をメインに表示する
  const mainName = dog.officialName || dog.name || '名称未設定';
  // メインに name を使っている場合は、どうぶつ名の重複表示を避ける
  const hasOfficialName = !!dog.officialName;
  const animalName = dog.name;

  const registrationEntries = showRegistrationNumbers
    ? buildRegistrationEntries(dog)
    : [];

  return (
    <VStack align="start" gap={size === 'lg' ? 1 : 0.5}>
      <Text
        fontWeight="bold"
        fontSize={MAIN_FONT_SIZE[size]}
        lineHeight="short"
        wordBreak="break-word"
      >
        {mainName}
      </Text>

      {showAnimalName && hasOfficialName && animalName && (
        <Text fontSize={SUB_FONT_SIZE[size]} color="gray.600">
          どうぶつ名: {animalName}
        </Text>
      )}

      {registrationEntries.length > 0 && (
        <HStack gap={2} flexWrap="wrap">
          {registrationEntries.map((entry) => (
            <Box key={entry.label}>
              <Text as="span" fontSize="xs" color="gray.500">
                {entry.label}:{' '}
              </Text>
              <Text as="span" fontSize="xs" color="gray.700" fontFamily="mono">
                {entry.value}
              </Text>
            </Box>
          ))}
        </HStack>
      )}
    </VStack>
  );
};

export default DogIdentityLabel;
